import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, ArrowRight, Mail, Lock, Sprout } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LandingLanguagePicker from "@/components/LandingLanguagePicker";

/**
 * AuthModal — email/password sign-in + sign-up form with a Google fallback.
 * Wired into the Landing hero and header via `setAuthOpen(true)`.
 *
 * Backend: POST /api/auth/signup and POST /api/auth/login return
 *   { user: <UserDoc> } and set the `session_token` cookie so that
 *   subsequent /api/auth/me calls succeed transparently.
 */
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthModal({ open, initialMode = "login", onClose }) {
  const { setUser } = useAuth();
  const [mode, setMode] = useState(initialMode);   // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Modal stays mounted (visibility toggled via `open`), so we must sync
  // `mode` with the parent's `initialMode` every time the modal opens.
  // Also clear stale error state so a login-error doesn't linger into a
  // fresh signup attempt.
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setError("");
    }
  }, [open, initialMode]);

  if (!open) return null;

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const url = mode === "signup" ? "/auth/signup" : "/auth/login";
      const body = mode === "signup"
        ? { email: email.trim().toLowerCase(), password, name: name.trim() || undefined }
        : { email: email.trim().toLowerCase(), password };
      const { data } = await api.post(url, body);
      setUser(data.user);
      toast.success(mode === "signup" ? "Welcome to Sprout" : "Welcome back");
      onClose?.();
      window.location.assign("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(20, 40, 30, 0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="auth-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl p-8 relative"
        style={{ background: "var(--bg-primary)", border: "1px solid var(--border-default)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          onClick={onClose}
          data-testid="auth-modal-close-btn"
        >
          <X size={18}/>
        </button>

        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[var(--accent-primary)] flex items-center justify-center">
              <Sprout className="text-white" size={17} strokeWidth={2}/>
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--text-secondary)]">
              {mode === "signup" ? "Create account" : "Welcome back"}
            </div>
          </div>
          <LandingLanguagePicker testId="auth-modal-lang" />
        </div>
        <h2
          className="text-2xl font-medium tracking-tight mt-2 mb-6"
          style={{ fontFamily: "var(--font-heading)" }}
          data-testid="auth-modal-title"
        >
          {mode === "signup" ? "Start your money reset" : "Sign in to Sprout"}
        </h2>

        <button
          className="btn-ghost w-full justify-center border-[var(--border-default)]"
          onClick={handleGoogle}
          data-testid="auth-modal-google-btn"
        >
          <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Continue with Google
        </button>

        <div className="my-5 flex items-center gap-3">
          <div className="flex-1 h-px bg-[var(--border-default)]"/>
          <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">or</span>
          <div className="flex-1 h-px bg-[var(--border-default)]"/>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="auth-modal-form">
          {mode === "signup" && (
            <div>
              <label className="label-eyebrow block mb-1.5">Name (optional)</label>
              <input
                className="field-input w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex"
                maxLength={80}
                data-testid="auth-modal-name"
              />
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-1.5">Email</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"/>
              <input
                type="email"
                className="field-input w-full pl-9"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="auth-modal-email"
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"/>
              <input
                type="password"
                className="field-input w-full pl-9"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                data-testid="auth-modal-password"
              />
            </div>
            {mode === "signup" && (
              <div className="text-[11px] text-[var(--text-muted)] mt-1">Min. 8 characters</div>
            )}
          </div>

          {error && (
            <div
              className="text-sm p-2.5 rounded-lg"
              style={{ background: "rgba(196,91,77,0.10)", color: "#8A2F22", border: "1px solid rgba(196,91,77,0.25)" }}
              data-testid="auth-modal-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary w-full justify-center"
            disabled={busy}
            data-testid="auth-modal-submit"
          >
            {busy ? "…" : (mode === "signup" ? "Create account" : "Sign in")}
            <ArrowRight size={15}/>
          </button>
        </form>

        <div className="mt-5 text-center text-sm text-[var(--text-secondary)]">
          {mode === "signup" ? (
            <>Already have an account?{" "}
              <button
                className="font-semibold text-[var(--accent-primary)] hover:underline"
                onClick={() => { setMode("login"); setError(""); }}
                data-testid="auth-modal-switch-login"
              >Sign in</button>
            </>
          ) : (
            <>New here?{" "}
              <button
                className="font-semibold text-[var(--accent-primary)] hover:underline"
                onClick={() => { setMode("signup"); setError(""); }}
                data-testid="auth-modal-switch-signup"
              >Create an account</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
